import { useCallback } from 'react';

import { get } from 'lodash';
import { useSelector } from 'react-redux';

import { cbBankLabelsMap as CBBANK_LABELS } from 'cbbank/inquiry/labels';
import { useFormConfig } from 'config/formConfig/hooks';
import { usePartnerConfig } from 'config/partner/hooks';
import { dzbLabelsMap as DZB_LABELS } from 'dzb/i18n/labels';
import { hausbankLabelsMap as HAUSBANK_LABELS } from 'hausbank/inquiry/labels';
import { editMarketValueReeProfitabillityLabelsMap } from 'modules/EditMarketValuesReeProfitability/editMarketValueReeProfitabillityLabel';
import { editMittweidaProfitabilityLabelMap } from 'modules/EditMarketValuesReeProfitability/editMittweidaProfitabilityLabel';
import {
  EXCLUDING_QUESTION_FIRST,
  FAVORED_DECISION_DATE,
  PROJECT_FINANCING__OBJECT_FUTURE_USAGE,
  USER_PROFILE_NEGATIVE_EQUITY_CAPITAL,
} from 'modules/Inquiry/Form/formFields';
import {
  LIQUIDITY_MONTHS_FOR_BIG_COMPANY,
  LIQUIDITY_MONTHS_FOR_SMALL_COMPANY,
} from 'modules/Inquiry/inquiry.constants';
import { additionalFieldsTranslations } from 'modules/Inquiry/inquiryFieldsTranslations/labelTranslator/additionalKeyTranslations';
import { beneficiaryOwnerDetailsLabelsMap } from 'modules/Inquiry/inquiryFieldsTranslations/labelTranslator/beneficiaryDetailsLabels';
import { companyDetailsLabelMap } from 'modules/Inquiry/inquiryFieldsTranslations/labelTranslator/companyDetailsLabels';
import {
  bfsServiceFinancingNeedLabelsMap,
  financingNeedLabelsMap,
} from 'modules/Inquiry/inquiryFieldsTranslations/labelTranslator/financingNeedLabels';
import {
  bfsSPeopleDetailsLabelsMap,
  peopleDetailsLabelsMap,
} from 'modules/Inquiry/inquiryFieldsTranslations/labelTranslator/peopleDetailsLabels';
import {
  bfsServiceRequestDetailsLabels,
  requestDetailsLabelsMap,
} from 'modules/Inquiry/inquiryFieldsTranslations/labelTranslator/requestDetailsLabels';
import { userProfileLabelsMap } from 'modules/Inquiry/inquiryFieldsTranslations/labelTranslator/userProfileLabels';
import { LabelsMap } from 'new/form/common/types';
import { SummaryType } from 'new/form/formatters/units';
import { onlineFactoringLabelsMap } from 'onlinefactoring/labels';
import { useTranslations } from 'utils/hooks/useTranslations';

import { bfsContactPersonLabelsMap } from './labelTranslator/bfsContactPersonLabels';
import { contractDetailsLabelsMap } from './labelTranslator/contractDetailsLabels';
import { mittweidaLabelsMap } from '../../../mittweida/i18n/labels';
import { mittweidaOperationLabelsMap } from '../../../mittweida/portals/operations/labels';
import { mmvLabels as MMV_LABELS } from '../../../mmv/i18n/labels';
import { useOptionalPortalConfig } from '../../../new/portals/state/portalConfigContext/hooks';
import { quickCheckLabelsMap } from '../../../pages/operationPortal/PlanningEvaluation/QuickCheck/QuickCheckLabelsMap';
import { getInquiryDetailsSelector } from '../../../store/inquiryDetails/selectors';
import {
  extractNestedFieldName,
  removeBracketsFromArrayFields,
} from '../../../utils/form/getFieldName';
import { PROJECT_FINANCING__OBJECT_USAGE } from '../Form/formFields';
import { InquiryType } from '../Inquiry.type';

const temporaryMonthNumber = `${LIQUIDITY_MONTHS_FOR_SMALL_COMPANY}/${LIQUIDITY_MONTHS_FOR_BIG_COMPANY}`;

const roleSpecificTranslations = [
  USER_PROFILE_NEGATIVE_EQUITY_CAPITAL,
  FAVORED_DECISION_DATE,
  EXCLUDING_QUESTION_FIRST,
];

const DEFAULT_LABELS: LabelsMap = {
  ...financingNeedLabelsMap,
  ...companyDetailsLabelMap,
  ...requestDetailsLabelsMap,
  ...userProfileLabelsMap,
  ...peopleDetailsLabelsMap,
  ...additionalFieldsTranslations,
  ...editMarketValueReeProfitabillityLabelsMap,
  ...quickCheckLabelsMap,
};

const MITTWEIDA_LABELS: LabelsMap = {
  ...mittweidaLabelsMap,
  ...mittweidaOperationLabelsMap,
  ...editMittweidaProfitabilityLabelMap,
};

const LEASEPLAN_LABELS: LabelsMap = {
  ...contractDetailsLabelsMap,
  ...beneficiaryOwnerDetailsLabelsMap,
  ...peopleDetailsLabelsMap,
};

const ONLINEFACTORING_LABELS: any = {
  ...onlineFactoringLabelsMap,
  ...companyDetailsLabelMap,
  ...userProfileLabelsMap,
  ...peopleDetailsLabelsMap,
};

const BFS_LABELS: LabelsMap = {
  ...financingNeedLabelsMap,
  ...companyDetailsLabelMap,
  ...requestDetailsLabelsMap,
  ...userProfileLabelsMap,
  ...peopleDetailsLabelsMap,
  ...additionalFieldsTranslations,
  ...bfsContactPersonLabelsMap,
};

const BFS_SERVICE_LABELS: LabelsMap = {
  ...DEFAULT_LABELS,
  ...beneficiaryOwnerDetailsLabelsMap,
  ...bfsServiceFinancingNeedLabelsMap,
  ...bfsServiceRequestDetailsLabels,
  ...bfsSPeopleDetailsLabelsMap,
};

const inquiryTypeLabelsMap: { [key in InquiryType]?: LabelsMap } = {
  [InquiryType.default]: DEFAULT_LABELS,
  [InquiryType.profiRLL]: DEFAULT_LABELS,
  [InquiryType.profiMittweida]: MITTWEIDA_LABELS,
  [InquiryType.onlinefactoring]: ONLINEFACTORING_LABELS,
  [InquiryType.leaseplan]: LEASEPLAN_LABELS,
  [InquiryType.bfs]: BFS_LABELS,
  [InquiryType.bfsService]: BFS_SERVICE_LABELS,
  [InquiryType.mmv]: MMV_LABELS,
  [InquiryType.dzb]: DZB_LABELS,
  [InquiryType.hausbank]: HAUSBANK_LABELS,
  [InquiryType.cbBank]: CBBANK_LABELS,
};

const commonInquiryTypeFinancingKeys = [
  ...Object.keys(financingNeedLabelsMap),
  PROJECT_FINANCING__OBJECT_USAGE,
];

const inquiryTypeFinancingKeys: { [key in InquiryType]?: string[] } = {
  default: commonInquiryTypeFinancingKeys,
  [InquiryType.profiRLL]: [
    ...commonInquiryTypeFinancingKeys,
    PROJECT_FINANCING__OBJECT_FUTURE_USAGE,
  ],
  [InquiryType.onlinefactoring]: [...Object.keys(onlineFactoringLabelsMap)],
  [InquiryType.bfsService]: Object.keys(bfsServiceFinancingNeedLabelsMap),
};

export const financingNeedKeys = (type: InquiryType): string[] =>
  inquiryTypeFinancingKeys[type] ?? inquiryTypeFinancingKeys.default!;

export const contractDetailsKeys = Object.keys(contractDetailsLabelsMap);
export const companyDetailsKeys = Object.keys(companyDetailsLabelMap);

export const useInquiryLabelTranslator = () => {
  const t = useTranslations();
  const {
    details: { fullName: partnerFullName },
  } = usePartnerConfig();
  const { selectedInquiryType } = useFormConfig();
  const labels = inquiryTypeLabelsMap[selectedInquiryType] ?? DEFAULT_LABELS;
  const { portalConfig } = useOptionalPortalConfig() || {};

  const inquiry = useSelector(getInquiryDetailsSelector);

  return useCallback(
    // FIXME
    // @ts-ignore
    (fieldKey) => {
      /*
       * For the old inquiry flow it is enough to extract the nested field name for field arrays.
       * This is how a nested field name looks like: 'project-financing-object-usages-future[0].type-of-use-future'
       * but the label of the field is just 'type-of-use-future
       */
      const oldFieldNamingScheme = extractNestedFieldName(fieldKey);

      /*
       * For the new inquiry flow the fieldName contains the parent of the field name but should not use the indexed
       * bracket notation that is used for array fields. This is how a nest field name looks like in the new flow:
       * 'financingNeedPage.objectInformationSection.futureUsage[0].typeOfUse' but the actual label here is:
       * 'financingNeedPage.objectInformationSection.futureUsage.typeOfUse'
       */
      const newFieldNamingScheme = removeBracketsFromArrayFields(fieldKey);

      /*
       * For fields that have a label sent from the backend we use that label instead of the one from the frontend.
       */
      const fieldConfig = (get(portalConfig?.summaryFields, newFieldNamingScheme) ||
        get(portalConfig?.summaryFields, oldFieldNamingScheme)) as SummaryType;

      /*
       * If we do not find a label with the new field name schema we look into the labels map with the old naming schema.
       * One of this should always be successful or else there is another error why a label is not found.
       */
      let label =
        fieldConfig?.label?.(inquiry) ||
        labels[newFieldNamingScheme] ||
        labels[oldFieldNamingScheme];
      if (!label) {
        // If the label is not found at this point we try to find the label defined for the section field, as we are probably looking at an array field.
        const arrayField = fieldKey.split('.').slice(0, -1).join('.');
        label = labels[arrayField];
      }

      const roleSpecific = roleSpecificTranslations.includes(newFieldNamingScheme)
        ? { roleSpecific: true }
        : undefined;
      if (!label) {
        return '';
      }
      return t(label, {
        months: temporaryMonthNumber,
        legalName: partnerFullName,
        ...roleSpecific,
      });
    },
    [inquiry, labels, partnerFullName, portalConfig?.summaryFields, t],
  );
};
